import apiService from '../infrastructure/api.service.js';
import authStorageService from "./auth-storage.service.js";
import loginContextService from '../security/login-context.service.js';

export const authService = {
    login,
    verifyOtp,
    resendOtp,
    logout,
    activateCustomerAccount,
    activateAgentAccount,
    passwordReset,
    forgotPassword
};


async function login(username, password, organizationName) {
    // Collect device/network context for fraud detection
    const fingerprint = await loginContextService.collect();

    const loginData = {
        "operation": "authenticate",
        "accessToken": null,
        "requestBody": {
            "username": username,
            "password": password,
            "organizationName": organizationName
        },
        "fingerprint": fingerprint
    };

    // eslint-disable-next-line no-useless-catch
    try {
        // Use custom request for auth endpoint
        const response = await apiService.customRequest('/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(loginData)
        });
        
        return response;
    } catch (error) {
        // Re-throw the error to maintain the error chain
        throw error;
    }
}

/**
 * Verify OTP for 2FA login
 * @param {string} username - User's email/phone
 * @param {string} organizationName - Organization name
 * @param {string} otp - 6-character OTP code
 * @returns {Promise<Object>} Authentication response with user data
 */
async function verifyOtp(username, organizationName, otp) {
    // Collect device/network context for fraud detection (same device check)
    const fingerprint = await loginContextService.collect();

    const verifyData = {
        "username": username,
        "organizationName": organizationName,
        "otp": otp.toUpperCase(),
        "fingerprint": fingerprint
    };

    try {
        const response = await apiService.customRequest('/login/verify-otp', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(verifyData)
        });
        
        return response;
    } catch (error) {
        throw error;
    }
}

/**
 * Resend OTP for 2FA login
 * @param {string} username - User's email/phone
 * @param {string} organizationName - Organization name
 * @returns {Promise<Object>} Response with masked email
 */
async function resendOtp(username, organizationName) {
    const resendData = {
        "username": username,
        "organizationName": organizationName
    };

    try {
        const response = await apiService.customRequest('/login/resend-otp', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(resendData)
        });
        
        return response;
    } catch (error) {
        throw error;
    }
}

async function activateAgentAccount(token, password) {
    const activationData = {
        "operation": "activate agent",
        "accessToken": "",
        "requestBody": {
            "token": token,
            "password": password
        }
    };

    return apiService.post('/registration/activate/agent', activationData);
}

async function activateCustomerAccount(token) {
    return apiService.get(`/registration/confirm?token=${token}`);
}

async function forgotPassword(email) {
    const resetData = {
        "operation": "ResetPasswordRequest",
        "requestBody": {
            "userName": email
        }
    };

    return apiService.post('/user/resetUserPassword', resetData);
}

async function passwordReset(userName, otp, newPassword) {
    const resetData = {
        "operation": "resetPasswordInitiate",
        "requestBody": {
            "userName": userName,
            "otp": otp,
            "password": newPassword
        }
    };

    return apiService.post('/user/setNewUserPassword', resetData);
}

function logout() { 
    // Clear all authentication data using the storage service
    authStorageService.clearAuthData();
}